import { FAMILY_HISTORY_MATERNAL_PATERNAL } from '@constants/extension-codes';
import type { FamilyHistoryRelationship } from '@constants/family-history';
import { relationships } from '@constants/family-history';
import { FAMILY_HISTORY_RELATIONSHIP_OPTIONS } from '@constants/option-types/family-history';

import { formatFhirDate, getFhirDate } from '@utils/fhir';
import { capitalize } from '@utils/format';

import type { Annotation, FamilyMemberHistoryCondition } from 'generated/types';
import type {
  FamilyMemberHistoryModel,
  FamilyMemberHistoryModelInput,
} from 'generated/types';
import { FamilyMemberHistoryStatus } from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type FamilyMemberCondition = {
  name?: string;
  diagnosedYear?: string | null;
  ageDiagnosed?: string | null;
  /**
   * The stored FHIR condition this row was read from, carried as Formik row
   * metadata (never rendered, never sent to the server directly). Gives each
   * row a stable identity through add/delete/rename - independent of array
   * position or name - so the save path can restore this row's own sibling
   * fields (note[1...], outcome, contributedToDeath, onsetAge, onsetRange)
   * and tell an untouched value from one the curator actually entered.
   * Absent for a condition the curator added this session.
   *
   * Carrying the object per row is what keeps clinical data off the wrong
   * condition: nothing is matched back by array index or by name, so adding,
   * deleting, reordering or renaming rows cannot transfer another condition's
   * fields onto this one.
   */
  _original?: FamilyMemberHistoryCondition;
};

/**
 * The condition fields the Age/Date Diagnosed form rows own and may write.
 *
 * `note` is included because Age Diagnosed lives in `note[0]` - see
 * `resolveConditionNotes` for how the rest of the array is preserved.
 */
export type DiagnosedFields = Partial<
  Pick<FamilyMemberHistoryCondition, 'note' | 'onsetPeriod'>
>;

/**
 * Age Diagnosed and Date Diagnosed are independent product values that must be
 * able to coexist (VEG-709). They are stored in two different elements so they
 * never contend for the `condition.onset[x]` choice type:
 *
 *   Age Diagnosed  -> condition.note[0].text
 *   Date Diagnosed -> condition.onsetPeriod.start
 *
 * Each is written only when the curator supplied it: an Age-only condition gets
 * no onset, and a Date-only condition gets no Age note invented for it.
 *
 * `original` supplies the stored condition so `note[1...]` survive - pass it
 * whenever one exists (the update path); the create path has none.
 */
export function resolveDiagnosedFields(
  ageDiagnosed: string | null | undefined,
  diagnosedYear: string | null | undefined,
  original?: Pick<FamilyMemberHistoryCondition, 'note'>
): DiagnosedFields {
  const note = resolveConditionNotes(ageDiagnosed, original);
  return {
    ...(note && { note }),
    ...(diagnosedYear && { onsetPeriod: { start: diagnosedYear } }),
  };
}

/**
 * Rebuild `condition.note[]` for one form row.
 *
 * `note[0]` is the Age Diagnosed slot and is the only element this form owns:
 * setting Age replaces it, clearing Age drops it, and `note[1...]` are copied
 * through untouched either way. When the age is unchanged the original
 * annotation is passed through verbatim rather than rebuilt, so any authorship
 * metadata (`id`, `authorString`, `time`, `authorReference`) on a record Vitall
 * did not write survives a save that never touched the field. A curator edit
 * writes a bare `{ text }` instead - inheriting a previous author's attribution
 * for a value they did not write would be worse than losing it.
 *
 * Returns `undefined` (rather than `[]`) when nothing is left, so `stripNulls`
 * doesn't send an empty array.
 */
export function resolveConditionNotes(
  ageDiagnosed: string | null | undefined,
  original?: Pick<FamilyMemberHistoryCondition, 'note'>
): Annotation[] | undefined {
  const storedNotes = original?.note ?? [];
  // `.at(0)` rather than destructuring so the empty-array case stays visible
  // to the type checker.
  const head = storedNotes.at(0);
  const rest = storedNotes.slice(1);

  if (ageDiagnosed) {
    const ageNote = head?.text === ageDiagnosed ? head : { text: ageDiagnosed };
    return [ageNote, ...rest];
  }
  return rest.length > 0 ? rest : undefined;
}

/**
 * Age Diagnosed as stored on a condition.
 *
 * `note[0].text` is the canonical location and always wins. `onsetString` is
 * read only as a display fallback: Vitall wrote Age Diagnosed there before
 * VEG-709, so a legacy record would otherwise render an empty Age field.
 *
 * Known and deliberate limitation: FHIR records no provenance for either
 * element, so this cannot tell a legacy Vitall age from arbitrary external
 * onset text ("unkown", "during childhood", "at birth"), nor a Vitall-written
 * `note[0]` from a foreign clinician's commentary. The fallback is therefore
 * confined to display - `isCuratorAuthoredAge` gates whether a read-back value
 * is ever allowed to be written into `note[0]`, so nothing gets reclassified
 * as Age Diagnosed just by being displayed in that field.
 */
export function readAgeDiagnosed(
  condition: Pick<FamilyMemberHistoryCondition, 'note' | 'onsetString'>
): string | undefined {
  return condition.note?.at(0)?.text ?? condition.onsetString ?? undefined;
}

/**
 * Display name of a condition, derived identically wherever conditions are
 * matched or listed so both sides of a comparison agree.
 */
export function getConditionName(
  condition: Pick<FamilyMemberHistoryCondition, 'code'>
): string | undefined {
  return condition.code.coding
    ?.map(({ display }) => display)
    .flat()
    .join('/');
}

/**
 * Where a row's displayed Age Diagnosed came from, and what may be done with
 * it on save. Computed once per condition.
 */
type AgeProvenance = {
  /** True when the curator authored this value, so it may be written to note[0]. */
  isAuthored: boolean;
  /**
   * True when the value being written supersedes an `onsetString` that was
   * only being displayed as Age Diagnosed for legacy compatibility, so that
   * onsetString should not also be kept.
   */
  supersedesLegacyOnsetString: boolean;
};

function resolveAgeProvenance(
  ageDiagnosed: string | null | undefined,
  original: FamilyMemberHistoryCondition | undefined
): AgeProvenance {
  // A condition the curator added this session: nothing stored to misinterpret.
  if (!original) {
    return { isAuthored: true, supersedesLegacyOnsetString: false };
  }
  // Already stored in the canonical slot, so the curator owns it - rewriting
  // it with the same text is a no-op. Any onsetString on such a record is an
  // ordinary onset[x] value that was never displayed as the age, so it is not
  // superseded by anything the form writes.
  if (original.note?.at(0)?.text !== undefined) {
    return { isAuthored: true, supersedesLegacyOnsetString: false };
  }
  // No note[0]: whatever the Age field displayed came from onsetString.
  const displayed = original.onsetString || undefined;
  const isAuthored = (ageDiagnosed || undefined) !== displayed;
  return {
    isAuthored,
    // Only once the curator has actually authored a replacement does the
    // legacy value stop being the record's Age Diagnosed. Until then it stays
    // exactly where it is.
    supersedesLegacyOnsetString: isAuthored && displayed !== undefined,
  };
}

/**
 * True when `ageDiagnosed` may be written into `note[0]` as Vitall's Age
 * Diagnosed - i.e. when the curator actually authored it.
 *
 * Three cases qualify: a condition added this session (nothing stored to
 * misinterpret), a condition that already stores its age in `note[0]` (the
 * canonical slot), and a value the curator changed from whatever was displayed.
 *
 * What does not qualify is the case this gate exists for: a bare `onsetString`
 * that `readAgeDiagnosed` surfaced for display and the curator left alone.
 * FHIR records no provenance, so a legacy Vitall age and arbitrary external
 * onset text (a discern-sourced "unkown", "during childhood", "at birth") are
 * indistinguishable from the condition alone. Promoting that into `note[0]`
 * would fabricate an Age Diagnosed the curator never asserted, so
 * `resolveConditionInput` leaves it in `onsetString` instead.
 */
export function isCuratorAuthoredAge(
  ageDiagnosed: string | null | undefined,
  original: FamilyMemberHistoryCondition | undefined
): boolean {
  return resolveAgeProvenance(ageDiagnosed, original).isAuthored;
}

/** The onset[x] variants a condition may carry; at most one may be populated. */
type OnsetVariants = Pick<
  FamilyMemberHistoryCondition,
  'onsetAge' | 'onsetPeriod' | 'onsetRange' | 'onsetString'
>;

/** A stored field counts as present unless it is null or undefined. */
function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Rebuilds one outgoing condition from its form row, preserving whatever the
 * form doesn't own - siblings (outcome, contributedToDeath, onsetAge,
 * onsetRange) and `note[1...]` - by reading them off `original`, the row's own
 * stored condition. `original` is only absent for a condition the curator
 * added this session, which owns nothing to preserve.
 *
 * The form owns exactly two elements: `note[0]` (Age Diagnosed) and
 * `onsetPeriod` (Date Diagnosed). Everything else is passed through.
 */
function resolveConditionInput(
  name: string | undefined,
  ageDiagnosed: string | null | undefined,
  diagnosedYear: string | null | undefined,
  original: FamilyMemberHistoryCondition | undefined
): FamilyMemberHistoryCondition {
  // Computed once for this condition: a displayed-but-unattributable
  // onsetString stays where it is instead of being promoted into note[0].
  const { isAuthored, supersedesLegacyOnsetString } = resolveAgeProvenance(
    ageDiagnosed,
    original
  );
  const writableAge = isAuthored ? ageDiagnosed : undefined;

  const diagnosed = resolveDiagnosedFields(
    writableAge,
    diagnosedYear,
    original
  );

  // onsetPeriod is the form's slot. When Date Diagnosed is set it is the sole
  // onset[x] variant, so any stored onsetAge/onsetRange/onsetString is dropped
  // - FHIR forbids two, and the backend now rejects a payload carrying both.
  // When it is not set, the stored variant is carried through, except the
  // onsetPeriod the curator just cleared and a legacy onsetString the curator
  // has now replaced with an explicit note[0] age.
  const preservedOnset: OnsetVariants = diagnosed.onsetPeriod
    ? {}
    : {
        ...(isPresent(original?.onsetAge) && { onsetAge: original.onsetAge }),
        ...(isPresent(original?.onsetRange) && {
          onsetRange: original.onsetRange,
        }),
        ...(isPresent(original?.onsetString) &&
          !supersedesLegacyOnsetString && {
            onsetString: original.onsetString,
          }),
      };

  return {
    code: {
      text: name,
      coding: [
        {
          display: name,
          system: FamilyHistoryRecord.VITALL_SYSTEM,
        },
      ],
    },
    ...(isPresent(original?.outcome) && { outcome: original.outcome }),
    ...(isPresent(original?.contributedToDeath) && {
      contributedToDeath: original.contributedToDeath,
    }),
    ...preservedOnset,
    ...diagnosed,
  };
}

/** The `deceased[x]` variants this form may write; at most one is populated. */
export type DeceasedFields = Partial<
  Pick<FamilyMemberHistoryModelInput, 'deceasedBoolean' | 'deceasedDate'>
>;

/**
 * FamilyMemberHistory.deceased[x] only allows one variant at a time.
 * Use date over boolean when both are present.
 */
export function resolveDeceasedFields(
  isDeceased: boolean | null | undefined,
  deceasedDate: string | null | undefined
): DeceasedFields {
  if (deceasedDate) {
    return { deceasedDate };
  }
  if (typeof isDeceased === 'boolean') {
    return { deceasedBoolean: isDeceased };
  }
  return {};
}

export type FamilyHistoryValues = Pick<
  FamilyHistoryRecord,
  | 'dateOfBirth'
  | 'deceasedDate'
  | 'description'
  | 'isDeceased'
  | 'maternalPaternal'
  | 'relationshipCode'
  | 'relationshipName'
  | 'selfReported'
> & {
  conditions: FamilyMemberCondition[];
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & FamilyHistoryValues;

export class FamilyHistoryRecord extends FhirResource {
  protected model: FamilyMemberHistoryModel;

  public constructor(model: FamilyMemberHistoryModel) {
    super(model);
    this.model = model;
  }

  public get maternalPaternal() {
    const extension = this.findExtension(FAMILY_HISTORY_MATERNAL_PATERNAL);
    return extension?.valueString;
  }

  public get relationship(): {
    category: FamilyHistoryRelationship | 'other';
    name: string;
  } | null {
    const coding = this.model.relationship.coding?.at(0);
    if (!coding?.code || !coding.display) {
      return null;
    }
    const { code, display } = coding;
    if (relationships.maternal.includes(code)) {
      return { category: 'maternal', name: display };
    } else if (relationships.paternal.includes(code)) {
      return { category: 'paternal', name: display };
    } else if (relationships.nuclear.includes(code)) {
      return { category: 'nuclear', name: display };
    }
    return { category: 'other', name: display };
  }

  public get relationshipCode() {
    return this.model.relationship.coding?.map(({ code }) => code).join(', ');
  }

  public set relationshipCode(value: string | null | undefined) {
    const relation = FAMILY_HISTORY_RELATIONSHIP_OPTIONS.find(
      ({ code }) => code === value
    );
    this.model = {
      ...this.model,
      relationship: value
        ? {
            coding: [
              {
                code: value,
                display:
                  relation?.display ?? this.relationshipName?.toLowerCase(),
                system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
              },
            ],
            text: relation?.text ?? this.relationshipName,
          }
        : {},
    };

    if (
      relation?.category === 'maternal' ||
      relation?.category === 'paternal' ||
      relation?.category === 'nuclear'
    ) {
      this.addOrReplaceExtension({
        url: FAMILY_HISTORY_MATERNAL_PATERNAL,
        valueString: capitalize(relation.category),
      });
    } else {
      this.removeExtension(FAMILY_HISTORY_MATERNAL_PATERNAL);
    }
  }

  public get relationshipName() {
    return this.model.relationship.coding
      ?.map(({ display }) => display)
      .join(', ');
  }

  public set relationshipName(value: string | null | undefined) {
    this.model = {
      ...this.model,
      relationship: value
        ? {
            coding: [
              {
                code: this.relationshipCode,
                display: value.toLowerCase(),
                system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
              },
            ],
            text: value,
          }
        : {},
    };
  }

  public get conditions() {
    const conditions = this.model.condition?.map<FamilyMemberCondition>(
      (condition) => ({
        name: getConditionName(condition),
        ageDiagnosed: readAgeDiagnosed(condition),
        diagnosedYear: condition.onsetPeriod?.start ?? undefined,
        // Cloned so the row metadata can't be mutated out from under
        // this.model by anything downstream (e.g. stripEmptyStrings, which
        // strips empty values from Formik's values object in place).
        _original: JSON.parse(JSON.stringify(condition)),
      })
    );
    return conditions ?? [];
  }

  public set conditions(value: FamilyMemberCondition[]) {
    this.model = {
      ...this.model,
      condition: value.map(({ ageDiagnosed, diagnosedYear, name, _original }) =>
        resolveConditionInput(name, ageDiagnosed, diagnosedYear, _original)
      ),
    };
  }

  public get deceasedDate() {
    // Date or year
    return this.model.deceasedDate;
  }

  public set deceasedDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      deceasedDate: value,
    };
  }

  public get isDeceased() {
    return this.model.deceasedBoolean;
  }

  public set isDeceased(value: boolean | null | undefined) {
    this.model = {
      ...this.model,
      deceasedBoolean: value,
    };
  }

  public get dateOfBirth() {
    return this.model.bornDate;
  }

  public set dateOfBirth(value: string | null | undefined) {
    this.model = {
      ...this.model,
      bornDate: value,
    };
  }

  public get description() {
    return this.model.note?.map(({ text }) => text).join(', ');
  }

  public set description(value: string | null | undefined) {
    this.model = {
      ...this.model,
      note: value
        ? [
            {
              text: value,
            },
          ]
        : null,
    };
  }

  public get endDate() {
    const cond = this.model.condition?.at(0);
    return cond?.onsetPeriod?.end;
  }

  public getOnsetDate() {
    // Only onsetPeriod.start is a calendar date. onsetString carries free text
    // (arbitrary onset descriptions from external sources, and Age Diagnosed on
    // records written before VEG-709), so it must never be parsed as a date -
    // and neither must note[0], which is now where Age Diagnosed lives.
    const cond = this.model.condition?.at(0);
    return getFhirDate(cond?.onsetPeriod?.start);
  }

  public getDateOfBirth() {
    return formatFhirDate(this.model.bornDate, {
      showTime: false,
      adjustTz: false,
    });
  }

  /**
   * Build the mutation input for this record from the current form values.
   *
   * Pure with respect to the instance: the field setters are applied to a
   * snapshot that is restored before returning, so `this.model` is exactly
   * what it was on the way in. A rejected mutation therefore leaves the record
   * untouched, and a retry (or a Formik reinitialize) re-derives the input
   * from the real stored model plus whatever the form holds at that moment,
   * rather than from a half-applied earlier attempt.
   */
  public getModelInput(
    values?: FamilyHistoryValues
  ): FamilyMemberHistoryModelInput {
    const storedModel = this.model;
    try {
      if (values) {
        // eslint-disable-next-line guard-for-in
        for (const key in values) {
          try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this[key] = values[key];
          } catch {
            // eslint-disable-next-line no-continue
            continue;
          }
        }
      }
      // The current form values are authoritative: this.model.deceasedDate can
      // lag behind them (e.g. a setter that never ran because its key was
      // absent from `values`, or was skipped by the catch above), so an
      // explicit isDeceased: false or a cleared deceasedDate must not lose to
      // whatever this.model still has. Both slots are blanked before
      // resolveDeceasedFields writes back at most one, so a stale value cannot
      // survive alongside the resolved one and only a single valid
      // deceased[x] variant goes out. Only fall back to this.model when
      // called without values at all.
      const isDeceased = values
        ? values.isDeceased
        : this.model.deceasedBoolean;
      const deceasedDate = values
        ? values.deceasedDate
        : this.model.deceasedDate;

      const cleaned = FhirResource.getCleanInput({
        ...this.model,
        deceasedBoolean: undefined,
        deceasedDate: undefined,
        ...resolveDeceasedFields(isDeceased, deceasedDate),
      });

      return {
        ...cleaned,
        status: FamilyMemberHistoryStatus.completed,
      };
    } finally {
      this.model = storedModel;
    }
  }
}
